import { useEffect, useState } from 'react';
import {
  MapContainer as MapContainerRaw,
  TileLayer as TileLayerRaw,
  Marker as MarkerRaw,
  Tooltip as TooltipRaw,
  Polygon as PolygonRaw,
  useMap,
} from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { fetchOpenAIP, googleMapsUrl, OpenAIPClass } from '../utils/airspace-helpers';
import SiteMapLayer from '../Events/SiteMapLayer';
import type { SiteMapShare } from '../Events/useSiteMapShare';
import SegTrack from '../components/ui/SegTrack';
import UiTooltip from '../components/ui/Tooltip';
import Alert from '../components/ui/Alert';
import { MAP_TIP, MapTipStyle } from '../components/ui/PopoverMapTip';

// react-leaflet 3.x types don't expose all leaflet props (attribution, sticky,
// icon). Cast to permissive components — runtime is fine.
const MapContainer = MapContainerRaw as any;
const TileLayer = TileLayerRaw as any;
const Marker = MarkerRaw as any;
const Tooltip = TooltipRaw as any;
const Polygon = PolygonRaw as any;

interface Props {
  launchLat: number;
  launchLng: number;
  audienceLat?: number | null;
  audienceLng?: number | null;
  /** Optional producer site map share — when ready, hull/rings/labels render and the map auto-frames to the show area. */
  siteMapShare?: SiteMapShare;
}

// Tile layer options. Satellite is the default for pilots; the dark basemap is
// available as a fallback (better for reading place names).
const TILE_LAYERS = {
  satellite: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
    attribution: '&copy; Esri, Maxar, Earthstar Geographics, USDA, USGS, AeroGRID',
    label: 'Satellite',
    maxZoom: 19,
  },
  // Hybrid layer overlays place names on top of the imagery — applied above
  // the satellite tiles when satellite mode is selected.
  satellite_labels: {
    url: 'https://server.arcgisonline.com/ArcGIS/rest/services/Reference/World_Boundaries_and_Places/MapServer/tile/{z}/{y}/{x}',
    attribution: '',
    label: 'Labels',
    maxZoom: 19,
  },
  dark: {
    url: 'https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png',
    attribution: '&copy; OpenStreetMap, &copy; CartoDB',
    label: 'Dark',
    maxZoom: 19,
  },
};

const launchIcon = L.divIcon({
  html: `<div style="width:20px;height:20px;background:#fbbf24;border:2px solid #000;border-radius:50%;box-shadow:0 0 0 2px rgba(255,255,255,0.95),0 0 8px rgba(0,0,0,0.6);"></div>`,
  className: '',
  iconSize: [20, 20],
  iconAnchor: [10, 10],
});
const audienceIcon = L.divIcon({
  html: `<div style="width:14px;height:14px;background:#a855f7;border:2px solid #fff;border-radius:50%;box-shadow:0 0 0 1px rgba(0,0,0,0.5);"></div>`,
  className: '',
  iconSize: [14, 14],
  iconAnchor: [7, 7],
});

// Sectional-chart-style fill colors per ICAO airspace class.
// Letters match the values produced by `deriveAirspaceClass()` in airspace-helpers.
const CLASS_COLORS: Record<string, { fill: string; stroke: string }> = {
  A:    { fill: '#1e3a8a', stroke: '#1d4ed8' }, // Class A — deep blue (high altitude)
  B:    { fill: '#1d4ed8', stroke: '#3b82f6' }, // Class B — blue (major airports)
  C:    { fill: '#9a3412', stroke: '#f97316' }, // Class C — orange/burnt
  D:    { fill: '#1e40af', stroke: '#60a5fa' }, // Class D — lighter blue
  E:    { fill: '#831843', stroke: '#ec4899' }, // Class E — magenta (sectional convention)
  F:    { fill: '#374151', stroke: '#bbbbbb' }, // Class F — gray (uncommon in US)
  G:    { fill: '#14532d', stroke: '#22c55e' }, // Class G — green (uncontrolled)
  SUA:  { fill: '#78350f', stroke: '#f59e0b' }, // Special Use — amber
  R:    { fill: '#7f1d1d', stroke: '#ef4444' }, // Restricted — red
  P:    { fill: '#581c87', stroke: '#a855f7' }, // Prohibited — purple
  D2:   { fill: '#7c2d12', stroke: '#f97316' }, // Danger area — orange
  default: { fill: '#374151', stroke: '#5f5f5f' },
};

function FitBounds({ launchLat, launchLng, audienceLat, audienceLng, siteMapBounds }: Pick<Props, 'launchLat' | 'launchLng' | 'audienceLat' | 'audienceLng'> & { siteMapBounds?: any }) {
  const map = useMap();
  useEffect(() => {
    // Priority: site map bounds (when present) → launch+audience → launch only.
    // Cap fitBounds at zoom 12 so a tight site doesn't snap to street level
    // and lose nearby class-boundary context.
    if (siteMapBounds?.sw && siteMapBounds?.ne) {
      const bounds = L.latLngBounds([
        [siteMapBounds.sw[1], siteMapBounds.sw[0]],
        [siteMapBounds.ne[1], siteMapBounds.ne[0]],
      ]);
      map.fitBounds(bounds, { padding: [80, 80], maxZoom: 12 });
    } else if (audienceLat && audienceLng) {
      const bounds = L.latLngBounds([
        [launchLat, launchLng],
        [audienceLat, audienceLng],
      ]);
      map.fitBounds(bounds, { padding: [80, 80], maxZoom: 12 });
    } else {
      map.setView([launchLat, launchLng], 11);
    }
  }, [launchLat, launchLng, audienceLat, audienceLng, siteMapBounds, map]);
  return null;
}

const AirspaceMap: React.FC<Props> = ({ launchLat, launchLng, audienceLat, audienceLng, siteMapShare }) => {
  const [airspaces, setAirspaces] = useState<OpenAIPClass[]>([]);
  const [airspacesError, setAirspacesError] = useState<string | null>(null);
  const [tileMode, setTileMode] = useState<'satellite' | 'dark'>('satellite');

  useEffect(() => {
    const dLat = 25 / 60;
    const cosL = Math.max(0.01, Math.cos(launchLat * Math.PI / 180));
    const dLng = (25 / 60) / cosL;
    const bbox = {
      minLat: launchLat - dLat,
      maxLat: launchLat + dLat,
      minLng: launchLng - dLng,
      maxLng: launchLng + dLng,
    };
    fetchOpenAIP(bbox)
      .then(setAirspaces)
      .catch((e: any) => setAirspacesError(e?.message || 'OpenAIP unavailable'));
  }, [launchLat, launchLng]);

  const baseLayer = TILE_LAYERS[tileMode];

  return (
    <div className="relative h-full w-full">
      <MapContainer
        center={[launchLat, launchLng]}
        zoom={11}
        scrollWheelZoom
        attributionControl={false}
        className="h-full w-full"
        style={{ background: '#0b1220' }}
      >
        <TileLayer
          attribution={baseLayer.attribution}
          url={baseLayer.url}
          maxZoom={baseLayer.maxZoom}
        />
        {/* Place-name overlay on top of satellite imagery */}
        {tileMode === 'satellite' && (
          <TileLayer
            attribution=""
            url={TILE_LAYERS.satellite_labels.url}
            maxZoom={TILE_LAYERS.satellite_labels.maxZoom}
            opacity={0.9}
          />
        )}
        <FitBounds
          launchLat={launchLat}
          launchLng={launchLng}
          audienceLat={audienceLat ?? null}
          audienceLng={audienceLng ?? null}
          siteMapBounds={siteMapShare?.state === 'ready' ? siteMapShare.bounds : null}
        />

        {/* Producer site map overlay — hull, rings, polygons, labels, images. */}
        {siteMapShare && <SiteMapLayer share={siteMapShare} />}

        {/* Airspace class polygons */}
        {airspaces.map((a, i) => {
          const colors = CLASS_COLORS[a.type] || CLASS_COLORS.default;
          const coords = polygonCoords(a.geometry);
          if (!coords) return null;
          return (
            <Polygon
              key={i}
              positions={coords}
              pathOptions={{
                color: colors.stroke,
                weight: 1.4,
                opacity: 0.9,
                fillColor: colors.fill,
                // Slightly more opaque over satellite so the class still reads
                fillOpacity: tileMode === 'satellite' ? 0.22 : 0.18,
              }}
            >
              <Tooltip sticky className={MAP_TIP}>
                <div className="text-xs">
                  <div className="font-semibold">Class {a.type}</div>
                  <div>{a.name}</div>
                  {a.floor_ft != null && a.ceiling_ft != null && (
                    <div className="text-on-surface-variant">{a.floor_ft} – {a.ceiling_ft} ft</div>
                  )}
                </div>
              </Tooltip>
            </Polygon>
          );
        })}

        <Marker position={[launchLat, launchLng]} icon={launchIcon}>
          <Tooltip className={MAP_TIP}>Launch</Tooltip>
        </Marker>
        {audienceLat && audienceLng && (
          <Marker position={[audienceLat, audienceLng]} icon={audienceIcon}>
            <Tooltip className={MAP_TIP}>Audience</Tooltip>
          </Marker>
        )}
      </MapContainer>
      <MapTipStyle />

      {/* Map controls — top right, mirrors EventList map control style.
          Stacked vertically so the tile toggle and "open in Google Maps"
          jump-out button stay independently clickable without overlapping. */}
      <div className="absolute top-2 right-2 z-[20] flex flex-col items-end gap-2">
        <SegTrack
          label="Map layer"
          size="sm"
          value={tileMode}
          onChange={(k) => setTileMode(k as 'satellite' | 'dark')}
          options={(['satellite', 'dark'] as const).map(mode => ({ key: mode, label: TILE_LAYERS[mode].label }))}
          className="shadow-lg"
        />
        <UiTooltip label="Open this location in Google Maps">
          <a
            href={googleMapsUrl(launchLat, launchLng)}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md bg-surface-container-high shadow-lg text-[11px] font-medium text-on-surface hover:text-white hover:bg-surface-container-highest transition-colors"
          >
            Google Maps <span aria-hidden="true">↗</span>
          </a>
        </UiTooltip>
      </div>

      {airspacesError && (
        <div className="absolute bottom-2 left-2 z-[20] max-w-[260px] rounded-control bg-surface-container-high pointer-events-none">
          <Alert variant="note" tone="warning" title="OpenAIP unavailable.">
            Class polygons not shown. Check `REACT_APP_OPENAIP_API_KEY` is set in Vercel.
          </Alert>
        </div>
      )}
    </div>
  );
};

function polygonCoords(geom: any): [number, number][] | [number, number][][] | null {
  if (!geom || !geom.type) return null;
  if (geom.type === 'Polygon') {
    return geom.coordinates[0].map((c: number[]) => [c[1], c[0]] as [number, number]);
  }
  if (geom.type === 'MultiPolygon') {
    return geom.coordinates.map((poly: any) =>
      poly[0].map((c: number[]) => [c[1], c[0]] as [number, number])
    );
  }
  return null;
}

export default AirspaceMap;
